import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { FeatureSpecsEntities, FeatureSpecsPaths, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { MoreAboutMMYLink } from 'site-modules/shared/components/more-about-mmy-link/more-about-mmy-link';
import { BrandHero } from 'site-modules/shared/components/brand-hero/brand-hero';
import { FeaturesSpecsTable } from 'site-modules/features-specs/components/features-specs-table/features-specs-table';
import { InventoryButton } from 'site-modules/shared/components/inventory-button/inventory-button';
import { FeaturesSpecsCta } from 'site-modules/features-specs/components/features-specs-cta/features-specs-cta';
import { FeaturesTrimBox } from 'site-modules/features-specs/components/features-trim-box/features-trim-box';

import './style-features-specs.scss';

function StyleFeaturesSpecsUI({
  vehicle,
  style,
  popularStyles,
  vehicleName,
  params,
  visitorLocation,
  onStyleChange,
  pageName,
  isMobile,
}) {
  if (!style) {
    return null;
  }

  const { isUsed } = params;
  const { styleAttributes } = style;
  const isElectric = !!get(styleAttributes, 'electric');
  const isPluginHybrid = !!get(styleAttributes, 'pluginElectric');

  const currentStyle = popularStyles.find(({ id }) => id === style.id);

  return (
    <div className="style-features-specs pos-r">
      <BrandHero contentClassName="pt-0_75 pt-md-1_5" isMobile={isMobile}>
        <Row>
          <Col xs={12} md={9} className="px-0">
            <div className="header-container px-0_5 px-md-0">
              <h1
                className={classnames('title fw-bold text-white mb-0_25 mb-md-0_5', {
                  'size-30': isMobile,
                  'size-36': !isMobile,
                })}
              >
                {vehicleName} - Specs & Features
              </h1>
              <div>
                <MoreAboutMMYLink
                  vehicle={vehicle}
                  className="text-white"
                  linkClassName={null}
                  iconClassName={null}
                  linkToReview={isUsed}
                  showBuyOnlineBtn={isUsed}
                  isMobile={isMobile}
                  params={params}
                />
              </div>
            </div>
          </Col>
        </Row>
      </BrandHero>

      <Container className="style-features-container">
        <Row className="mb-2">
          <Col xs={12} lg={7}>
            <FeaturesTrimBox
              className="mb-2"
              params={params}
              style={style}
              showTaxRebates={isPluginHybrid || isElectric}
              popularStyles={popularStyles}
              onStyleChange={onStyleChange}
            />
            {params.isUsed ? (
              <div className="d-flex">
                <InventoryButton
                  params={params}
                  makeModelSubmodelYear={vehicle}
                  visitorLocation={visitorLocation}
                  className="fw-medium"
                  btnColor="blue-50"
                />
                <InventoryButton
                  params={params}
                  makeModelSubmodelYear={vehicle}
                  visitorLocation={visitorLocation}
                  className="fw-medium"
                  btnColor="blue-50"
                  isBuyOnline
                />
              </div>
            ) : (
              <FeaturesSpecsCta
                vehicle={vehicle}
                styleId={style.id}
                trimName={currentStyle?.trim}
                leadFormCreativeId={getLeadFormCreativeId({
                  isNewVin: true,
                  pageNameLegacy: pageName,
                  formName: LEAD_FORMS_CREATIVE_ID.FEATURE_SPECS,
                })}
                dataTrackingParent="features-specs-cta1"
              />
            )}
          </Col>
        </Row>

        <FeaturesSpecsTable
          style={style}
          visitorLocation={visitorLocation}
          vehicle={vehicle}
          trimName={currentStyle?.trim}
          params={params}
          isMobile={isMobile}
          pageName={pageName}
        />
      </Container>
    </div>
  );
}

StyleFeaturesSpecsUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  popularStyles: VehicleEntities.PopularStyles.isRequired,
  params: FeaturesSpecsParams.isRequired,
  vehicleName: PropTypes.string.isRequired,
  style: FeatureSpecsEntities.FeatureStyleEntity,
  visitorLocation: VisitorEntities.Location,
  onStyleChange: PropTypes.func,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
};

StyleFeaturesSpecsUI.defaultProps = {
  style: null,
  visitorLocation: null,
  onStyleChange: undefined,
  pageName: null,
  isMobile: false,
};

export const propsAreEqual = (prevProps, { style }) => !style;
export const StyleFeaturesSpecs = connectToModel(React.memo(StyleFeaturesSpecsUI, propsAreEqual), {
  style: bindToPath(({ styleId }) => styleId && FeatureSpecsPaths.buildStyleFeaturesPath(styleId), VehicleModel),
  visitorLocation: bindToPath('location', VisitorModel),
});
